import React from 'react';
import { Card, Row, Col, Typography, Divider, Tag, Button, Steps } from 'antd';
import { 
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import './DetailPage.css';

const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;

interface DetailPageProps {
  onBack: () => void;
}

const DetailPage: React.FC<DetailPageProps> = ({ onBack }) => {
  const domains = [
    {
      title: '古陶瓷鑑定',
      description: '專業的古陶瓷真偽鑑定、年代判斷、窯口識別服務',
      features: ['真偽鑑定', '年代判斷', '窯口識別', '價值評估'],
      color: '#1890ff'
    },
    {
      title: '古書畫鑑定',
      description: '書畫作品的作者歸屬、真偽判斷、藝術價值分析',
      features: ['作者鑑定', '真偽判斷', '藝術風格分析', '歷史價值評估'],
      color: '#52c41a'
    },
    {
      title: '古玉器鑑定',
      description: '玉器材質分析、工藝鑑定、文化內涵解讀',
      features: ['材質分析', '工藝鑑定', '文化解讀', '收藏價值評估'],
      color: '#faad14'
    },
    {
      title: '數字智理科技',
      description: 'AI智能諮詢系統，結合傳統鑑定與現代科技',
      features: ['智能識別', '專家諮詢', '數據分析', '報告生成'],
      color: '#722ed1'
    }
  ];

  const procedures = [
    {
      title: '鑑識階段',
      description: '通過專業技術手段進行初步鑑定',
      details: [
        '準確性評估：運用科學方法確保鑑定結果的準確性',
        '確定性分析：建立可靠的判斷依據和標準',
        '準值測定：獲得精確的測量數據和參考值',
        '容錯機制：建立預約制度，允許合理的誤差範圍',
        '有界條件：在規模限制內提供專業服務'
      ],
      status: 'process'
    },
    {
      title: '推介階段',
      description: '基於鑑定結果提供市場分析和建議',
      details: [
        '市場行情分析：了解當前市場價格趨勢和收藏熱點',
        '潮流預測：預判未來市場走向和投資價值',
        '交易建議：提供收購或出售的專業建議',
        '種類分析：詳細記錄品名、款識、紋飾、尺寸、重量等信息',
        '特徵描述：分析用料、手感、聲音、搬運、收藏等特點',
        '來源追溯：調查來歷、相關文獻及收藏者背景'
      ],
      status: 'process'
    },
    {
      title: '評估階段',
      description: '綜合評價和專業建議',
      details: [
        '保護價值評估：確定文物的保護等級和重要性',
        '需求分析：明確客戶的具體需求和期望',
        '專業點評：提供詳細的專業評價和建議',
        '內容修正：對初步結論進行修正和完善',
        '表達建議：以專業術語準確表達鑑定結果',
        '依據說明：提供判斷的科學依據和理論支撐'
      ],
      status: 'wait'
    }
  ];

  const rules = [
    {
      category: '預約規則',
      items: [
        '提前24小時預約，確保專家時間安排',
        '提供清晰的文物照片或3D圖像',
        '詳細描述文物的基本信息和來源',
        '準備相關的歷史文獻和收藏證明'
      ]
    },
    {
      category: '鑑定標準',
      items: [
        '遵循國際文物鑑定標準和規範',
        '採用多重驗證方法確保準確性',
        '結合傳統經驗與現代科技手段',
        '建立完整的鑑定檔案和記錄'
      ]
    },
    {
      category: '服務承諾',
      items: [
        '提供詳細的鑑定報告和證書',
        '對鑑定結果承擔專業責任',
        '保護客戶隱私和文物信息安全',
        '提供後續諮詢和技術支持'
      ]
    }
  ];

  return (
    <div className="detail-page">
      <div className="detail-header">
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />} 
          onClick={onBack}
          size="large"
          className="back-button"
        >
          返回主頁
        </Button>
        <Title level={1} style={{ textAlign: 'center', margin: '40px 0' }}>
          AI Agent 智能諮詢詳情
        </Title>
      </div>

      {/* 領域介紹 */}
      <div className="section">
        <Title level={2}>專業領域</Title>
        <Row gutter={[24, 24]}>
          {domains.map((domain, index) => (
            <Col xs={24} md={12} key={index}>
              <Card className="domain-card" style={{ borderLeft: `4px solid ${domain.color}` }}>
                <Title level={4} style={{ color: domain.color }}>
                  {domain.title}
                </Title>
                <Paragraph>{domain.description}</Paragraph>
                <div className="feature-tags">
                  {domain.features.map((feature, idx) => (
                    <Tag key={idx} color={domain.color} style={{ margin: '4px' }}>
                      {feature}
                    </Tag>
                  ))}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* 診斷流程 */}
      <div className="section">
        <Title level={2}>診斷流程</Title>
        <Card className="process-card">
          <Steps direction="vertical" current={1}>
            {procedures.map((procedure, index) => (
              <Step
                key={index}
                title={procedure.title}
                description={
                  <div>
                    <Paragraph>{procedure.description}</Paragraph>
                    <ul className="procedure-details">
                      {procedure.details.map((detail, idx) => (
                        <li key={idx}>{detail}</li>
                      ))}
                    </ul>
                  </div>
                }
                status={procedure.status as any}
                icon={
                  procedure.status === 'process' ? <ClockCircleOutlined /> :
                  procedure.status === 'finish' ? <CheckCircleOutlined /> :
                  <ExclamationCircleOutlined />
                }
              />
            ))}
          </Steps>
        </Card>
      </div>

      {/* 規則制度 */}
      <div className="section">
        <Title level={2}>規則制度</Title>
        <Row gutter={[24, 24]}>
          {rules.map((rule, index) => (
            <Col xs={24} md={8} key={index}>
              <Card className="rule-card">
                <Title level={4}>{rule.category}</Title>
                <ul className="rule-list">
                  {rule.items.map((item, idx) => (
                    <li key={idx}>
                      <Text>{item}</Text>
                    </li>
                  ))}
                </ul>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* 聯繫信息 */}
      <div className="section">
        <Card className="contact-card">
          <Title level={3} style={{ textAlign: 'center' }}>
            聯繫我們
          </Title>
          <Row gutter={[48, 24]} style={{ textAlign: 'center' }}>
            <Col xs={24} md={8}>
              <Title level={4}>AI智元AI研究室</Title>
              <Paragraph>專業的文物鑑定研究機構</Paragraph>
            </Col>
            <Col xs={24} md={8}>
              <Title level={4}>服務時間</Title>
              <Paragraph>週一至週五 9:00-18:00<br />週末及節假日預約服務</Paragraph>
            </Col>
            <Col xs={24} md={8}>
              <Title level={4}>技術支持</Title>
              <Paragraph>2025年最新AI技術<br />傳統與現代完美結合</Paragraph>
            </Col>
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default DetailPage;